<template>
	<ion-app>
		<Z-fullscreen-loading v-if="!isInitialized" />
		<ion-router-outlet v-else :animated="false" />
	</ion-app>

	<VueQueryDevtools />
</template>

<script setup>
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from './store'
import { getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/plugins/w/auth/store/auth.store'
import ZFullscreenLoading from '@/plugins/app@components/fullscreen-loading/z-fullscreen-loading.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { transformData } from '@/plugins/app/api/refactor/request.service'
import ResultDetail from '@/plugins/app@lz/result/detail'
import wAxios from '@/plugins/w/axios/models/axios'

// TODO HINTS PREFETCH
// import { fetchHints } from '../_api/_services/app.service'
import axios from '@/plugins/w/axios/models/axios'
import { useToast } from '@/plugins/w/toast/_composeables'
import { usePrefetch } from '../composables/prefetch.composable'
import { ApplicationMode, useApplication } from '../composables/application.composable'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const authStore = useAuthStore()
const toast = useToast()
const queryClient = useQueryClient()

const isInitialized = ref(false)
const globalProperties = getCurrentInstance().appContext.config.globalProperties

const { prefetchFirst, prefetchAll } = usePrefetch()
const { mode } = useApplication()

onMounted(async () => {
	if (mode.value === ApplicationMode.PAYMENT) {
		isInitialized.value = true
	} else {
		await initializeApplication()
	}
})

const initializeApplication = async () => {
	const { eventBus } = globalProperties
	eventBus.on('app:prefetch-data', prefetchAll)

	if (route.name == 'Login wit JWT token') {
		await onJwtLogin()
			.then(() => {
				window.location.href = `${import.meta.env.BASE_URL}prihlasenie/vyber-pristupu`
			})
			.catch(() => {
				authStore.logout(false)
				window.location.href = import.meta.env.BASE_URL
			})

		return
	}

	try {
		const { maintenance_mode, user_authenticated } = await axios.get_auth_data(`v1/maintenance-status`)

		// Handle maintenance mode
		if (maintenance_mode) {
			setTimeout(() => router.replace({ name: '503' }))
			isInitialized.value = true
			return
		}

		// Handle not authenticated user
		if (!user_authenticated) {
			authStore.logout(false)

			setTimeout(() => {
				router.replace({ name: 'Login' })
				isInitialized.value = true
			})

			return
		}

		// Handle redirect from maintenance mode
		if (route.name == '503' && !maintenance_mode) {
			router.replace({ name: 'Dashboard' })
		}

		// Skip prefetch if user has to change password and accept terms first
		if (route.name == 'Login - Podmienky') {
			isInitialized.value = true
			return
		}

		if (route.query.requestId) getRequestById(route.query.requestId)

		await prefetchFirst()
		isInitialized.value = true

		prefetchAll(true)
	} catch (error) {
		console.log('App.js mounted error - ', error)
		isInitialized.value = true
		return
	}
}

const getRequestById = async (id) => {
	try {
		const data = await wAxios.get_auth_data(`v1/requests/detail?order_external_id=${encodeURIComponent(id)}`)
		if (!data) return

		const newData = await transformData(data)
		await openResultDetail({ row: newData, resultsOptional: [newData] })
	} catch (error) {
		if (error.status == 404) {
			toast.error('No result was found for this account.')
		}
	}
}

const openResultDetail = async ({ row, resultsOptional = [] }) => {
	// return
	const ResultDetailModal = await ResultDetail.create({
		componentProps: {
			presetFilters: LIS_REQUESTS_FILTERS_DEFAULTS,
			presetPagination: PAGINATION_DEFAULTS,
			presetRequests: resultsOptional.length > 0 ? resultsOptional : results?.value?.data,
			requestOrderId: row.order_id
		}
	})

	ResultDetailModal.present()

	await ResultDetailModal.onDidDismiss()
	appStore.asyncRefreshUnreadResults()
}

const onJwtLogin = async () => {
	return new Promise(async (resolve, reject) => {
		if (!route.query.jwt) return reject()

		try {
			authStore.setToken(route.query.jwt)
			await authStore.userInfo()

			const provider = authStore.getProviders[0]
			const access = provider.accesses[0]

			if (!provider || !access) {
				toast.error('Používateľ nemá priradeného žiadneho poskytovateľa')
				return reject()
			}

			authStore.setLzAccess({ access, provider })
			resolve()
		} catch (error) {
			reject()
		}
	})
}

watch(
	() => route.path,
	() => {
		setTimeout(() => {
			appStore?.refresh()
		}, 5000) // This is loading only notifications for now
	}
)
</script>
